import React, { useState, FormEvent, ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  Bars3Icon, 
  UserIcon, 
  NewspaperIcon,
  InformationCircleIcon,
  ArrowRightOnRectangleIcon 
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

const Navbar = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <nav className="bg-white shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-primary">DizQueTem</span>
          </Link>

          {/* Search Bar - Hidden on mobile */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-2xl mx-8">
            <div className="relative w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar estabelecimentos..."
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-primary hover:text-accent"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>
            </div>
          </form>

          {/* Navigation Links - Hidden on mobile */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/news" className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2">
              <NewspaperIcon className="h-5 w-5" />
              Notícias
            </Link>
            {user ? (
              <>
                <Link to="/profile" className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2">
                  <UserIcon className="h-5 w-5" />
                  Perfil
                </Link>
                <Link to="/logout" className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2">
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  Sair
                </Link>
              </>
            ) : (
              <Link to="/login" className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2">
                <UserIcon className="h-5 w-5" />
                Entrar
              </Link>
            )}
            <Link to="/about" className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2">
              <InformationCircleIcon className="h-5 w-5" />
              Sobre
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-gray-600 hover:text-primary"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden py-4"
          >
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar estabelecimentos..."
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-primary hover:text-accent"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                </button>
              </div>
            </form>
            <div className="flex flex-col space-y-4">
              <Link
                to="/news"
                className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <NewspaperIcon className="h-5 w-5" />
                Notícias
              </Link>
              {user ? (
                <>
                  <Link
                    to="/profile"
                    className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <UserIcon className="h-5 w-5" />
                    Perfil
                  </Link>
                  <Link
                    to="/logout"
                    className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                    Sair
                  </Link>
                </>
              ) : (
                <Link
                  to="/login"
                  className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <UserIcon className="h-5 w-5" />
                  Entrar
                </Link>
              )}
              <Link
                to="/about"
                className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2"
                onClick={() => setIsMenuOpen(false)}
              >
                <InformationCircleIcon className="h-5 w-5" />
                Sobre
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 